/* import __COLOCATED_TEMPLATE__ from './content-typeahead.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { trackedFunction } from 'ember-resources/util/function';
import { setOwner } from '@ember/application';

import type Composer from '@intercom/composer-ember/lib/composer';
import { type TypeaheadMatchResult } from '@intercom/composer-ember';
import ContentServiceSearchAPI, {
  type ContentSearchParams,
} from 'embercom/lib/content-service/search-api';
import { EntityType } from 'embercom/models/data/entity-types';
import { KNOWLEDGE_HUB_ENTITIES } from 'embercom/lib/knowledge-hub/constants';
import { objectIcons } from 'embercom/models/data/matching-system/matching-constants';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';
import type ContentWrapper from 'embercom/models/content-service/content-wrapper';
import { ContentWrapperType } from 'embercom/models/content-service/content-wrapper';
import type IntlService from 'ember-intl/services/intl';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import type RegionService from 'embercom/services/region-service';
import { AiContentState } from 'embercom/lib/ai-content-library/constants';
import type ContentFragment from 'embercom/models/content-service/content-fragment';

interface Args {
  composer: Composer;
  matchResult: TypeaheadMatchResult | null;
  registerDelegate(f: Function): void;
  deregisterDelegate(f: Function): void;
}

interface Signature {
  Args: Args;
}

interface ContentItem {
  contentId: number;
  contentType: EntityType;
  title: string;
  icon: InterfaceIconName;
  isSelectable?: boolean;
}

const MAX_CONTENT_RESULTS = 8;
const PROSEMIRROR_SELECTOR = '.ProseMirror';
const KNOWLEDGE_HUB_URL_PATTERN = 'knowledge-hub/all-content';

// Content type to URL mapping for knowledge hub
const CONTENT_TYPE_URL_MAP: Record<EntityType, string> = {
  [EntityType.ArticleContent]: 'article',
  [EntityType.ExternalContent]: 'external_content',
  [EntityType.ContentSnippet]: 'content_snippet',
  [EntityType.FileSourceContent]: 'file',
  [EntityType.InternalArticle]: 'internal-article',
} as any;

// Content type to emoji mapping
const CONTENT_TYPE_EMOJI_MAP: Record<EntityType, string> = {
  [EntityType.ArticleContent]: '📄',
  [EntityType.ExternalContent]: '🌐',
  [EntityType.ContentSnippet]: '📝',
  [EntityType.FileSourceContent]: '📎',
  [EntityType.InternalArticle]: '🔒',
} as any;

export default class ContentTypeahead extends Component<Signature> {
  @service declare intercomEventService: any;
  @service declare appService: AppService;
  @service declare regionService: RegionService;
  @service declare intl: IntlService;

  searchApi: ContentServiceSearchAPI;
  private prosemirrorElement: Element | null = null;

  constructor(owner: unknown, args: Args) {
    super(owner, args);

    setOwner(this, owner);
    this.searchApi = new ContentServiceSearchAPI(owner);

    if (args.registerDelegate) {
      args.registerDelegate(this.keyboardEventDelegate);
    }

    // Set up click handler for existing and new links
    this.scheduleClickHandlerSetup();
  }

  private scheduleClickHandlerSetup() {
    // Set up click handler after DOM is ready
    setTimeout(() => this.setupLinkClickHandler(), 100);
  }

  private setupLinkClickHandler() {
    // Clean up any existing handler first
    this.cleanupLinkClickHandler();

    this.prosemirrorElement = document.querySelector(PROSEMIRROR_SELECTOR);
    if (this.prosemirrorElement) {
      this.prosemirrorElement.addEventListener('click', this.handleLinkClick);
    }
  }

  private cleanupLinkClickHandler() {
    if (this.prosemirrorElement) {
      this.prosemirrorElement.removeEventListener('click', this.handleLinkClick);
      this.prosemirrorElement = null;
    }
  }

  handleLinkClick = (event: MouseEvent) => {
    let target = event.target as HTMLElement;

    // Check if clicked element is a link or inside a link
    let link = target.closest('a[href]') as HTMLAnchorElement;

    if (link && link.href.includes(KNOWLEDGE_HUB_URL_PATTERN)) {
      event.preventDefault();
      event.stopPropagation();

      // Open in new tab
      safeWindowOpen(link.href, '_blank', 'noopener,noreferrer');
    }
  };

  get query() {
    if (!this.args.matchResult) {
      return '';
    }
    return this.args.matchResult.text.replace('@', '').trim();
  }

  private searchLoader = trackedFunction(this, async () => {
    let searchTerm = this.query;

    try {
      // Base search parameters
      let baseSearchParams: ContentSearchParams = {
        app_id: this.appService.app.id,
        per_page: MAX_CONTENT_RESULTS,
        object_types: KNOWLEDGE_HUB_ENTITIES.filter(
          (type) => type !== EntityType.ContentLibraryFolder,
        ),
      };

      // Add search term if provided — only set title, not content_terms.
      // This ensures the backend uses title_v2_terms (title-only search with fuzzy matching)
      // instead of content_query/knowledge_hub_search_query which search body text and
      // produce noisy results where web pages outrank articles with matching titles.
      if (searchTerm) {
        baseSearchParams.title = searchTerm;
      }

      // Create separate search params for Fin content and internal articles
      let chatbotSearchParams = {
        ...baseSearchParams,
        object_types: KNOWLEDGE_HUB_ENTITIES.filter(
          (type) => type !== EntityType.ContentLibraryFolder && type !== EntityType.InternalArticle,
        ),
        states: [1], // Published content only
        chatbot_state: AiContentState.USED_BY_FIN, // Only content enabled for AI Agent
        content_wrapper_types: [ContentWrapperType.SUPPORT_CONTENT], // Use knowledge hub search for better relevance
      };

      let internalArticleSearchParams = {
        ...baseSearchParams,
        object_types: [EntityType.InternalArticle],
        content_wrapper_types: [ContentWrapperType.SUPPORT_CONTENT],
      };

      // Make parallel API calls
      let [chatbotResponse, internalArticleResponse] = await Promise.allSettled([
        this.searchApi.contentSearch(chatbotSearchParams),
        this.searchApi.contentSearch(internalArticleSearchParams),
      ]);

      // Combine results from both calls
      let allContentWrappers: ContentWrapper[] = [];

      if (chatbotResponse.status === 'fulfilled' && chatbotResponse.value?.contentWrappers) {
        allContentWrappers.push(...chatbotResponse.value.contentWrappers);
      }

      if (
        internalArticleResponse.status === 'fulfilled' &&
        internalArticleResponse.value?.contentWrappers
      ) {
        allContentWrappers.push(...internalArticleResponse.value.contentWrappers);
      }

      // Sort by relevance if search term exists
      if (searchTerm && searchTerm.trim()) {
        allContentWrappers = this.sortByRelevance(allContentWrappers, searchTerm.trim());
      }

      // Limit to MAX_CONTENT_RESULTS
      let limitedWrappers = allContentWrappers.slice(0, MAX_CONTENT_RESULTS);

      // Map the response to our ContentItem interface
      return limitedWrappers.map((wrapper: ContentWrapper) => {
        return {
          contentId: this.getEntityIdFromContentWrapper(wrapper) || 0,
          contentType: wrapper.entityType || EntityType.ArticleContent,
          title:
            wrapper.title || this.intl.t('ai-agent.guidance.content-typeahead.untitled-content'),
          icon: this.getIconForEntityType(wrapper.entityType || EntityType.ArticleContent),
        };
      });
    } catch (error) {
      return [];
    }
  });

  get isLoading() {
    return this.searchLoader.isLoading;
  }

  get items() {
    if (!this.args.matchResult) {
      return [];
    }

    return this.searchLoader.value || [];
  }

  get displayItems() {
    if (!this.args.matchResult) {
      return [];
    }

    if (this.isLoading) {
      return [
        {
          contentId: 0,
          contentType: EntityType.ArticleContent,
          title: this.intl.t('ai-agent.guidance.content-typeahead.searching'),
          icon: 'search' as InterfaceIconName,
          isSelectable: false,
        },
      ];
    }

    let actualItems = this.items;
    if (actualItems.length > 0) {
      return actualItems.map((item) => ({ ...item, isSelectable: true }));
    }

    if (this.query) {
      return [
        {
          contentId: 0,
          contentType: EntityType.ArticleContent,
          title: this.intl.t('ai-agent.guidance.content-typeahead.no-results', {
            query: this.query,
          }),
          icon: 'search' as InterfaceIconName,
          isSelectable: false,
        },
      ];
    }

    return [];
  }

  private getEntityIdFromContentWrapper(wrapper: ContentWrapper): number | undefined {
    if (wrapper.entityType === EntityType.ArticleContent) {
      let content = wrapper.contents.find(
        (content: ContentFragment) => content.chatbotState === AiContentState.USED_BY_FIN,
      );
      if (content) {
        return content.contentId;
      }
    }
    return wrapper.entityId;
  }

  private getContentTypeForUrl(entityType: EntityType): string {
    // Map EntityType to the activeContentType URL parameter
    return CONTENT_TYPE_URL_MAP[entityType] || 'article'; // fallback
  }

  private getContentTypeDisplayName(entityType: EntityType): string {
    // Use the existing knowledge hub translation for content type display names
    return this.intl.t(`knowledge-hub.content-type-stamp.content-type.${entityType}`);
  }

  @action
  handleSelect(selectedItem: ContentItem) {
    // Only process selectable items
    if (selectedItem.isSelectable) {
      this.insertContentReference(selectedItem);
    }
  }

  @action
  insertContentReference(selectedItem: ContentItem) {
    if (!this.args.matchResult || !this.args.composer) {
      return;
    }

    try {
      // Track analytics
      this.intercomEventService.trackAnalyticsEvent({
        action: 'inserted',
        object: 'content_reference',
        place: 'fin_guidance',
        content_type: this.getContentTypeDisplayName(selectedItem.contentType),
      });

      // Create the reference text to insert with emoji
      let contentTypeName = this.getContentTypeDisplayName(selectedItem.contentType);
      let emoji = this.getEmojiForEntityType(selectedItem.contentType);
      let truncatedTitle = this.truncateTitle(selectedItem.title);
      let referenceText = `${emoji} ${contentTypeName}: ${truncatedTitle}`.trim();

      // Build the proper knowledge hub URL with embedded content info
      let baseUrl = this.regionService.intercomradesUrl(this.regionService.region);
      let contentType = this.getContentTypeForUrl(selectedItem.contentType);
      let articleUrl = `${baseUrl}/a/apps/${this.appService.app.id}/knowledge-hub/all-content?activeContentId=${selectedItem.contentId}&activeContentType=${contentType}&editorMode=view&contentId=${selectedItem.contentId}&contentTypeId=${selectedItem.contentType}`;

      // Get the ProseMirror state
      let prosemirrorState = this.args.composer.state.prosemirrorState;

      if (prosemirrorState) {
        let { tr, schema } = prosemirrorState;

        // Delete the @mention text
        tr.delete(this.args.matchResult.range.from, this.args.matchResult.range.to);

        // Insert the reference text
        tr.insertText(referenceText, this.args.matchResult.range.from);

        // Create marks for the inserted text
        let anchorMark = schema.marks.anchor.create({
          href: articleUrl,
        });

        // Apply anchor mark to the inserted text
        tr.addMark(
          this.args.matchResult.range.from,
          this.args.matchResult.range.from + referenceText.length,
          anchorMark,
        );

        // Remove stored marks so subsequent text doesn't inherit the formatting
        tr.removeStoredMark(anchorMark);

        // Apply the transaction
        this.args.composer.commands.dispatch(tr);

        // Add a space after and focus
        this.args.composer.commands.insertText(' ');
        this.args.composer.commands.focus();

        // Ensure click handler is set up for the newly inserted link
        this.scheduleClickHandlerSetup();
      }
    } catch (error) {
      console.error('ContentTypeahead: Error inserting content reference:', error);
    }
  }

  keyboardEventDelegate = (_event: KeyboardEvent) => {
    // Handle keyboard events for typeahead navigation if needed
    return false;
  };

  willDestroy() {
    super.willDestroy();

    if (this.args.deregisterDelegate) {
      this.args.deregisterDelegate(this.keyboardEventDelegate);
    }

    // Clean up click handler
    this.cleanupLinkClickHandler();
  }

  private getIconForEntityType(entityType: EntityType): InterfaceIconName {
    return objectIcons[entityType] || 'article'; // fallback to article icon
  }

  private getEmojiForEntityType(entityType: EntityType): string {
    // Get emoji/unicode representation for content types in inserted text
    return CONTENT_TYPE_EMOJI_MAP[entityType] || '📄'; // fallback to document emoji
  }

  private truncateTitle(title: string, maxLength = 50): string {
    if (title.length <= maxLength) {
      return title;
    }
    return `${title.substring(0, maxLength).trim()}...`;
  }

  private sortByRelevance(wrappers: ContentWrapper[], searchTerm: string): ContentWrapper[] {
    return wrappers.sort((a, b) => {
      let scoreA = this.calculateRelevanceScore(a, searchTerm);
      let scoreB = this.calculateRelevanceScore(b, searchTerm);
      return scoreB - scoreA; // Sort descending (highest score first)
    });
  }

  // Calculate relevance score for content items based on title similarity to search term
  private calculateRelevanceScore(wrapper: ContentWrapper, searchTerm: string): number {
    let score = 0;
    let title = wrapper.title?.toLowerCase() || '';
    let searchTermLower = searchTerm.toLowerCase();

    // Exact title match gets highest score
    if (title === searchTermLower) {
      score += 100;
    }
    // Title starts with search term gets high score
    else if (title.startsWith(searchTermLower)) {
      score += 80;
    }
    // Title contains search term gets medium score
    else if (title.includes(searchTermLower)) {
      score += 60;
    }
    // Content-only matches get lowest score (base score)
    else {
      score += 10;
    }

    return score;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Guidance::ContentTypeahead': typeof ContentTypeahead;
    'ai-agent/guidance/content-typeahead': typeof ContentTypeahead;
  }
}
